import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Button, Price } from '@/components/ui'
import { Check } from 'lucide-react'
import {
  CAPABILITIES,
  selfServeMonthly,
  capabilityNameList,
  dfyBundle,
  managedService,
  oneTimeAddOn,
} from '@gjl/voice/pricing'

// Interactive two-axis pricing: which capabilities, and who runs them.
// All numbers come from @gjl/voice/pricing; the only logic here is composition.

const launchBuild = oneTimeAddOn('launch-build')
const websiteCare = managedService('website-care')
const managedWebsite = managedService('managed-website')
const managedEmail = managedService('managed-email')
const growthPartner = dfyBundle('growth-partner')
const concierge = dfyBundle('concierge')
type Mode = 'diy' | 'help'
type WebsiteLevel = 'care' | 'managed'

interface LineItem {
  label: string
  amount: string
}

// PriceBundle prices are display strings ('$800/mo'); derive numbers for math.
const growthPartnerMonthly = Number(growthPartner.price.replace(/[^0-9]/g, ''))

const ONE_TIME_AMOUNT = launchBuild.price.replace(' one-time', '')
const ONE_TIME_LINE: LineItem = {
  label: `${launchBuild.name}: custom design, your content in, domain connected. Doubles as onboarding for done-for-you plans.`,
  amount: ONE_TIME_AMOUNT,
}

const CAPABILITY_OPTIONS = CAPABILITIES.map((c) => ({ id: c.id, label: c.name }))

export function PricingConfigurator() {
  const [selected, setSelected] = useState<Set<string>>(new Set(['website']))
  const [mode, setMode] = useState<Mode>('diy')
  const [websiteLevel, setWebsiteLevel] = useState<WebsiteLevel>('care')

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const result = useMemo(() => {
    const items: LineItem[] = []
    const notes: string[] = []
    let monthly = 0
    // When set, this range replaces the computed sum as the headline total
    // (Concierge engagements are a range, not a sum of parts).
    let totalDisplay: string | null = null

    const capIds = [...selected]

    if (selected.size === 0) {
      return { items, notes, monthly, totalDisplay, oneTime: null as LineItem | null }
    }

    if (mode === 'diy') {
      // Self-serve total is the sum of the real per-capability prices.
      for (const id of capIds) {
        const cap = CAPABILITIES.find((c) => c.id === id)!
        items.push({ label: cap.name, amount: `$${cap.priceMonthly}/mo` })
      }
      monthly = selfServeMonthly(capIds)
      // Name only what THIS customer selected, derived from the flag. The old
      // note listed all four then-team-run capabilities regardless of the
      // selection, so picking Ticketing alone also told you about donor
      // management, and the list went stale the day a capability shipped
      // self-serve.
      const teamRun = CAPABILITIES.filter((c) => capIds.includes(c.id) && !c.selfServeLive)
      if (teamRun.length > 0) {
        const verb = teamRun.length === 1 ? 'is' : 'are'
        notes.push(
          `${capabilityNameList(teamRun)} ${verb} set up and run for you by our team today, included in the capability price.`
        )
      }
      return { items, notes, monthly, totalDisplay, oneTime: ONE_TIME_LINE }
    }

    // Help mode: managed services per capability.
    const helped = new Set(capIds)
    if (helped.size >= 4) {
      // At this scope, summing managed services stops being honest: this is a
      // Concierge engagement, so the range IS the price.
      items.push({
        label: `${concierge.name}: everything run for you, plus strategy`,
        amount: concierge.price,
      })
      totalDisplay = concierge.price
      notes.push('Covers every capability you selected, fundraising and box office included.')
      notes.push(
        'Done-for-you engagements start with a one-time onboarding (the $250 Launch build counts) and a 3-month minimum.'
      )
      return { items, notes, monthly, totalDisplay, oneTime: ONE_TIME_LINE }
    }
    const websiteManaged = helped.has('website') && websiteLevel === 'managed'
    if (websiteManaged && helped.has('email')) {
      monthly += growthPartnerMonthly
      items.push({
        label: `${growthPartner.name} (${managedWebsite.name} + ${managedEmail.name})`,
        amount: growthPartner.price,
      })
      helped.delete('website')
      helped.delete('email')
    }
    if (helped.has('website')) {
      const svc = websiteLevel === 'care' ? websiteCare : managedWebsite
      monthly += svc.priceMonthly
      items.push({ label: svc.name, amount: `$${svc.priceMonthly}/mo` })
      helped.delete('website')
    }
    if (helped.has('email')) {
      monthly += managedEmail.priceMonthly
      items.push({ label: managedEmail.name, amount: `$${managedEmail.priceMonthly}/mo` })
      helped.delete('email')
    }
    for (const id of helped) {
      const cap = CAPABILITIES.find((c) => c.id === id)!
      monthly += cap.priceMonthly
      items.push({ label: `${cap.name} (set up and run by our team)`, amount: `$${cap.priceMonthly}/mo` })
    }
    if (capIds.includes('donor-management') || capIds.includes('ticketing') || capIds.includes('applications')) {
      notes.push(`Full fundraising, box-office, or festival execution lives in ${concierge.name} (${concierge.price}).`)
    }
    notes.push(
      'Done-for-you plans start with a one-time onboarding (the $250 Launch build counts) and a 3-month minimum.'
    )
    return { items, notes, monthly, totalDisplay, oneTime: ONE_TIME_LINE }
  }, [selected, mode, websiteLevel])

  return (
    <section className="mx-auto max-w-4xl px-5 md:px-12">
      {/* One plain ground for the tool, no accent border: the pills inside are
          the controls and keep their outlines; the frame around them does not
          need one too. */}
      <div className="bg-card rounded-md p-6 md:p-10">
        <h2 className="text-center text-2xl font-bold">Price it in ten seconds</h2>
        <p className="text-muted-foreground mt-2 text-center text-sm">
          Pick what you need and who runs it. Every price here is the real price.
        </p>

        {/* Step 1: capabilities */}
        <div className="mt-8">
          <div className="text-sm font-semibold text-white/90">1. What do you need?</div>
          <div className="mt-3 flex flex-wrap gap-2">
            {CAPABILITY_OPTIONS.map((opt) => (
              <button
                key={opt.id}
                type="button"
                onClick={() => toggle(opt.id)}
                aria-pressed={selected.has(opt.id)}
                className={`cursor-pointer rounded-full border px-4 py-2 text-sm transition ${
                  selected.has(opt.id)
                    ? 'border-primary bg-primary/20 text-white'
                    : 'border-border text-muted-foreground hover:border-primary/50'
                }`}
              >
                {selected.has(opt.id) && <Check className="mr-1 inline h-3.5 w-3.5" strokeWidth={3} />}
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Step 2: who runs it */}
        <div className="mt-6">
          <div className="text-sm font-semibold text-white/90">2. Who runs it?</div>
          <div className="mt-3 flex gap-2">
            {(
              [
                { value: 'diy', label: 'I will run it' },
                { value: 'help', label: 'Run it for me' },
              ] as { value: Mode; label: string }[]
            ).map((opt) => (
              <button
                key={opt.value}
                type="button"
                onClick={() => setMode(opt.value)}
                aria-pressed={mode === opt.value}
                className={`cursor-pointer rounded-full border px-4 py-2 text-sm transition ${
                  mode === opt.value
                    ? 'border-primary bg-primary/20 text-white'
                    : 'border-border text-muted-foreground hover:border-primary/50'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          {mode === 'help' && selected.has('website') && (
            <div className="mt-3 flex flex-wrap items-center gap-2 text-sm">
              <span className="text-muted-foreground">Website support:</span>
              {(
                [
                  { value: 'care', label: `One update a month ($${websiteCare.priceMonthly})` },
                  { value: 'managed', label: `Fully managed ($${managedWebsite.priceMonthly})` },
                ] as { value: WebsiteLevel; label: string }[]
              ).map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setWebsiteLevel(opt.value)}
                  aria-pressed={websiteLevel === opt.value}
                  className={`cursor-pointer rounded-full border px-3 py-1.5 transition ${
                    websiteLevel === opt.value
                      ? 'border-primary bg-primary/20 text-white'
                      : 'border-border text-muted-foreground hover:border-primary/50'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Result */}
        <div className="pricing-result-panel bg-surface-raised mt-8 rounded-md p-6">
          {result.items.length === 0 ? (
            <p className="text-muted-foreground text-center text-sm">Pick at least one capability above.</p>
          ) : (
            <>
              <div className="text-sm font-semibold text-white/70">Ongoing monthly</div>
              <ul className="mt-2 space-y-2 text-sm">
                {result.items.map((item) => (
                  <li key={item.label} className="flex items-baseline justify-between gap-4">
                    <span className="text-foreground/90">{item.label}</span>
                    <Price size="sm">{item.amount}</Price>
                  </li>
                ))}
              </ul>
              {/* The total separates from the lines above by space and size, not a rule. */}
              <div className="mt-6 flex flex-wrap items-baseline justify-between gap-x-4">
                <span className="text-foreground font-semibold">Your monthly</span>
                <Price size="lg">{result.totalDisplay ?? `$${result.monthly}/mo`}</Price>
              </div>
              {result.oneTime && (
                <div className="mt-8">
                  <div className="text-sm font-semibold text-white/70">One-time setup (not monthly)</div>
                  <div className="mt-2 flex items-baseline justify-between gap-4 text-sm">
                    <span className="text-foreground/90">{result.oneTime.label}</span>
                    <Price size="sm" note="once">
                      {result.oneTime.amount}
                    </Price>
                  </div>
                </div>
              )}
              {result.notes.map((note) => (
                <p key={note} className="text-muted-foreground mt-2 text-xs">
                  {note}
                </p>
              ))}
              <p className="text-muted-foreground mt-2 text-xs">Pay annually and get 2 months free.</p>
              <div className="mt-5 flex flex-col justify-center gap-3 sm:flex-row">
                <Link to="/get-started">
                  <Button className="w-full cursor-pointer sm:w-auto">Get Started</Button>
                </Link>
                <a href="/book" target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" className="w-full cursor-pointer sm:w-auto">
                    Book a Free Call
                  </Button>
                </a>
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  )
}
